import {
  FloatingToolbar as LiveblocksFloatingToolbar,
  Toolbar,
  type ToolbarBlockSelectorItem,
} from '@liveblocks/react-tiptap'
import { IconColumns, IconColumnsOff, IconListCheck } from '@tabler/icons-react'
import { getHTMLFromFragment } from '@tiptap/core'
import type { Editor } from '@tiptap/react'
import { type ReactNode, useCallback } from 'react'

import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'

function useCreateTaskFromSelection(editor: Editor | null): () => void {
  const setOpen = useTaskComposerStore((state) => state.setOpen)
  const setDefaultValues = useTaskComposerStore(
    (state) => state.setDefaultValues
  )
  const setOnTaskCreated = useTaskComposerStore(
    (state) => state.setOnTaskCreated
  )

  return useCallback(() => {
    if (!editor) {
      return
    }

    const { from, to } = editor.state.selection
    const slice = editor.state.doc.slice(from, to)
    const selectedHtml = getHTMLFromFragment(slice.content, editor.schema)

    if (!selectedHtml.trim()) {
      return
    }

    setOnTaskCreated((task) => {
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContentAt(from, {
          type: 'mention',
          attrs: {
            reference: task.reference.reference,
          },
        })
        .run()
    })

    setDefaultValues({
      title: selectedHtml,
    })
    setOpen(true)
  }, [editor, setOpen, setDefaultValues, setOnTaskCreated])
}

export interface FloatingToolbarProps {
  editor: Editor | null
  enableColumns?: boolean
}

export function FloatingToolbar({
  editor,
  enableColumns = false,
}: FloatingToolbarProps): ReactNode {
  const canSetColumns = enableColumns && editor?.can().setColumns(2)
  const canUnsetColumns = enableColumns && editor?.can().unsetColumns()
  const createTaskFromSelection = useCreateTaskFromSelection(editor)

  // Add checklist to the default block selector items
  const blockSelectorItems = useCallback(
    (defaultItems: ToolbarBlockSelectorItem[]): ToolbarBlockSelectorItem[] => {
      // Find ordered list index to insert checklist after it
      const orderedListIndex = defaultItems.findIndex(
        (item) => item.name === 'Numbered list'
      )
      const insertIndex =
        orderedListIndex !== -1 ? orderedListIndex + 1 : defaultItems.length

      const checklistItem: ToolbarBlockSelectorItem = {
        name: 'Checklist',
        icon: <IconListCheck className="size-4" />,
        isActive: (e) => e.isActive('taskList'),
        setActive: (e) => e.chain().focus().clearNodes().toggleTaskList().run(),
      }

      return [
        ...defaultItems.slice(0, insertIndex),
        checklistItem,
        ...defaultItems.slice(insertIndex),
      ]
    },
    []
  )

  return (
    <LiveblocksFloatingToolbar editor={editor}>
      <Toolbar.BlockSelector items={blockSelectorItems} />
      <Toolbar.SectionInline />

      {enableColumns && (
        <>
          <Toolbar.Separator />
          <Toolbar.Button
            name="Add columns"
            icon={<IconColumns className="size-4" />}
            onClick={() => editor?.chain().focus().setColumns(2).run()}
            disabled={!canSetColumns}
          />
          {canUnsetColumns && (
            <Toolbar.Button
              name="Remove columns"
              icon={<IconColumnsOff className="size-4" />}
              onClick={() => editor?.chain().focus().unsetColumns().run()}
            />
          )}
        </>
      )}

      <Toolbar.Separator />
      <Toolbar.Button
        name="Create task from selection"
        icon={<IconNewTask className="size-4" />}
        onClick={createTaskFromSelection}
        shortcut="⌘⇧O"
      />
    </LiveblocksFloatingToolbar>
  )
}
